import { addBackgroundToContent } from '@/lib/util';
import baseApi from '@/modules/services/base';
import {
  FramesTag,
  MyElearningDetailsTag,
  VideoLinkTag,
} from '@/modules/services/tags';
import { FrameDataType } from '@/modules/types/frame';
import { ElementType } from '../types/element';
import { RepositionEntityType } from '../types/helper';

export interface FrameQueryType extends FrameDataType {
  elearningId: string;
}

interface RepositionFrameQueryType {
  elearningId: string;
  framePositions: RepositionEntityType[];
}

const framesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    deleteFrame: build.mutation<FrameQueryType, Partial<FrameQueryType>>({
      query: (frame) => ({
        url: `frames/${frame.id}`,
        method: 'DELETE',
        body: { isDeleted: true },
      }),
      invalidatesTags: [FramesTag, MyElearningDetailsTag],
    }),
    getFrames: build.query<FrameDataType[], { elearningId: string }>({
      query: (param) => `frames?elearning-id=${param.elearningId}`,
      providesTags: [FramesTag],
    }),
    addFrame: build.mutation<FrameQueryType, Partial<FrameQueryType>>({
      query(frame) {
        const { elearningId } = frame;
        delete frame.elearningId;
        frame.description = '';
        return {
          url: `frames/${elearningId}`,
          method: 'POST',
          body: frame,
          responseType: 'json',
        };
      },
      invalidatesTags: [FramesTag, MyElearningDetailsTag],
    }),
    updateFrame: build.mutation<FrameQueryType, Partial<FrameQueryType>>({
      query(frame) {
        delete frame.elearningId;
        return {
          url: `frames/${frame.id}`,
          method: 'PUT',
          body: frame,
          responseType: 'json',
        };
      },
      invalidatesTags: [FramesTag, MyElearningDetailsTag, VideoLinkTag],
    }),
    duplicateFrame: build.mutation<FrameQueryType, Partial<FrameQueryType>>({
      query(frame) {
        const { elearningId } = frame;
        delete frame.elearningId;
        delete frame.id;
        delete frame.frameScript;
        frame.title += ' (copy)';
        return {
          url: `frames/${elearningId}`,
          method: 'POST',
          body: frame,
          responseType: 'json',
        };
      },
      invalidatesTags: [FramesTag, MyElearningDetailsTag],
    }),
    repositionFrame: build.mutation<
      RepositionFrameQueryType,
      Partial<RepositionFrameQueryType>
    >({
      query(payload) {
        const { elearningId, framePositions } = payload;

        return {
          url: `frames/${elearningId}/position`,
          method: 'POST',
          body: framePositions,
          responseType: 'json',
        };
      },
      invalidatesTags: [FramesTag, MyElearningDetailsTag],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFramesQuery,
  useAddFrameMutation,
  useUpdateFrameMutation,
  useRepositionFrameMutation,
  useDeleteFrameMutation,
  useDuplicateFrameMutation,
} = framesApi;

export default framesApi;
